import type { AxiosInstance } from "axios";

export type PointBounds = [number, number, number, number];

export interface ApiMapPoint {
  id: number;
  attributes: Record<string, any>;
}

interface PointPage {
  data: ApiMapPoint[];
  meta: { pagination: { page: number; pageCount: number; total: number } };
}

export class MapPointQuery {
  constructor(private readonly client: AxiosInstance, private readonly path: string) {}

  async getPoints(categoryId: number, locale: string, signal: AbortSignal): Promise<{ data: ApiMapPoint[] }> {
    const params: Record<string, string | number> = {
      "filters[ip_category][id][$eq]": categoryId,
      "pagination[pageSize]": 10000,
      "sort[0]": "id:asc",
      locale,
    };
    // Fetch every point, but only the attributes used by markers and POI modals.
    // Populating categories/counties and CMS timestamps for every pin adds no UI data.
    ["titlu", "adresa", "numar_telefon", "website", "latitudine", "longitudine"].forEach((field, index) => {
      params[`fields[${index}]`] = field;
    });
    if (categoryId === 5) {
      ["localitate", "proprietate_publica", "stare", "capacitate"].forEach((field, index) => {
        params[`populate[civil_shelter][fields][${index}]`] = field;
      });
    }

    const points: ApiMapPoint[] = [];
    const ids = new Set<number>();
    let total: number | undefined;
    let pageCount = 1;
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    if (signal.aborted) abort();
    const deadline = setTimeout(abort, 45000);
    const fetchPage = async (page: number) => {
      if (controller.signal.aborted) throw new DOMException("Map request canceled", "AbortError");
      const { data: response } = await this.client.get<PointPage>(this.path, {
        params: { ...params, "pagination[page]": page }, signal: controller.signal, timeout: 45000,
      });
      if (controller.signal.aborted) throw new DOMException("Map request canceled", "AbortError");
      const pagination = response.meta?.pagination;
      if (!Array.isArray(response.data) || !pagination || pagination.page !== page ||
          !Number.isInteger(pagination.pageCount) || pagination.pageCount < 0 ||
          !Number.isInteger(pagination.total) || pagination.total < 0) {
        throw new Error("Map points API returned invalid pagination.");
      }
      if (total !== undefined && (total !== pagination.total || pageCount !== pagination.pageCount)) {
        throw new Error("Map points changed during pagination; reload this category.");
      }
      total = pagination.total;
      pageCount = pagination.pageCount;
      return response.data;
    };
    try {
      const pages: ApiMapPoint[][] = [await fetchPage(1)];
      let nextPage = 2;
      // First obtain the server's actual page count; then use at most three
      // requests at once. Results stay ordered and are committed only when complete.
      const worker = async () => {
        while (nextPage <= pageCount) {
          const page = nextPage++;
          pages[page - 1] = await fetchPage(page);
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, Math.max(0, pageCount - 1)) }, worker));
      for (const point of pages.flat()) {
        if (ids.has(point.id)) throw new Error("Map points API returned duplicate identifiers.");
        ids.add(point.id);
        points.push(point);
      }
      if (points.length !== total) throw new Error("Map points API returned an incomplete category.");
      return { data: points };
    } finally {
      controller.abort();
      clearTimeout(deadline);
      signal.removeEventListener("abort", abort);
    }
  }
}
